import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import apiService from '../../services/ApiService';
import type { AwardImportPreview, AwardImportRow, AwardImportReceipt, ExternalVehicleAward } from '../../types/judging';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

const hasInvalidControls = (value?: string) => Array.from(value || '').some(char =>
  char.charCodeAt(0) < 32 && ![9, 10, 13].includes(char.charCodeAt(0)));

interface Props {
  vehicleId: number;
  onClose: () => void;
  onSaved: (awards: ExternalVehicleAward[]) => void;
}

export default function AwardImportModal({ vehicleId, onClose, onSaved }: Props) {
  const [text, setText] = useState('');
  const [preview, setPreview] = useState<AwardImportPreview | null>(null);
  const [rows, setRows] = useState<Array<AwardImportRow & { included: boolean }>>([]);
  const [busy, setBusy] = useState(false);
  const [attempted, setAttempted] = useState(false);
  const [receipt, setReceipt] = useState<AwardImportReceipt | null>(null);
  const [error, setError] = useState('');
  const selected = rows.filter(row => row.included);
  const valid = selected.length > 0 && selected.every(row => row.title.trim() && row.event_title.trim()
    && row.title.length <= 150 && row.event_title.length <= 150
    && (row.placement || '').length <= 50 && (row.location || '').length <= 150 && (row.notes || '').length <= 1000
    && ![row.title, row.event_title, row.placement, row.location, row.notes].some(hasInvalidControls)
    && typeof row.award_year === 'number' && Number.isInteger(row.award_year) && row.award_year >= 1900 && row.award_year <= new Date().getFullYear());
  const analyze = async () => {
    setBusy(true); setError('');
    try {
      const result = await apiService.analyzeAwardImport(vehicleId, text);
      setPreview(result); setRows(result.awards.map(row => ({ ...row, included: true })));
    } catch (e) { setError(e instanceof Error ? e.message : 'Unable to organize awards'); }
    finally { setBusy(false); }
  };
  const publish = async () => {
    if (!preview || !valid || busy) return;
    setBusy(true); setAttempted(true); setError('');
    try {
      const result = await apiService.publishAwardImport(vehicleId, preview.session_token, selected);
      setReceipt(result); onSaved(result.awards);
    } catch (e) { setError(e instanceof Error ? e.message : 'Unable to publish. Retry this review safely.'); }
    finally { setBusy(false); }
  };
  const change = (id: string, patch: Partial<AwardImportRow & { included: boolean }>) => {
    setRows(previous => previous.map(row => row.temporary_id === id ? { ...row, ...patch } : row));
  };
  return createPortal(
    <Modal isOpen size="lg" mobileFullscreen initialFocus="dialog" title="Import external awards"
      description="Admin only. Paste a list, review each win, then verify and publish."
      onClose={() => { if (!busy) onClose(); }} closeOnEscape={!busy} closeOnOverlayClick={!busy}
      footer={<div className="flex flex-wrap items-center gap-2">
        {receipt ? <Button onClick={onClose}>Done</Button> : preview ? <>
          <Button disabled={busy || !valid} onClick={() => void publish()}>{busy ? 'Publishing…' : attempted ? 'Retry publish safely' : `Verify and publish ${selected.length} awards`}</Button>
          {!attempted ? <Button variant="ghost" disabled={busy} onClick={() => { setPreview(null); setError(''); }}>Back to list</Button> : null}
        </> : <Button disabled={busy || !text.trim()} onClick={() => void analyze()}>{busy ? 'Organizing awards…' : 'Organize with AI'}</Button>}
        {!receipt ? <Button variant="ghost" disabled={busy} onClick={onClose}>Cancel</Button> : null}
      </div>}>
      {error ? <p role="alert" className="text-sm text-red-300 mb-4">{error}</p> : null}
      {receipt ? <p role="status" className="text-gray-200">{receipt.created} awards published. {receipt.skipped} already recorded and skipped.</p>
        : !preview ? <div><label className="block text-sm text-gray-300">Awards list
          <textarea className="input w-full mt-2 font-mono text-sm" rows={14} maxLength={16000} value={text} disabled={busy}
            placeholder={'2024\n- Car show - Best Audi & Best Bagged\n2025\n- Another show - Best of Show'}
            onChange={event => setText(event.target.value)} />
        </label><p className="block mt-2 text-xs text-gray-500">Include year headings and event names. Combined wins become separate awards; custom titles are welcome.</p>
        </div> : <div className="space-y-4">
          <p className="text-sm text-gray-300" role="status">{rows.length} awards found. Review the names, events, years and placements. Existing awards are skipped when publishing.</p>
          {preview.warning ? <p className="text-sm text-amber-300">{preview.warning}</p> : null}
          {attempted ? <p className="text-sm text-gray-400">This review is locked for safe retries. Retry if the connection failed.</p> : null}
          <fieldset disabled={busy || attempted} className="space-y-3">
            {rows.map((row, index) => <div key={row.temporary_id} className="rounded-xl border border-white/10 p-4 space-y-3">
              <label className="flex items-center gap-2 text-sm text-gray-200">
                <input type="checkbox" checked={row.included} onChange={event => change(row.temporary_id, { included: event.target.checked })} />
                Include award {index + 1}
              </label>
              <p className="text-xs text-gray-500 whitespace-pre-wrap break-words">{row.original_line}</p>
              {row.needs_review ? <p className="text-xs text-amber-300">Check missing or uncertain details before publishing.</p> : null}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label className="block text-sm text-gray-300">Award title {index + 1}<input className="input w-full" maxLength={150} value={row.title} onChange={event => change(row.temporary_id, { title: event.target.value })} /></label>
                <label className="block text-sm text-gray-300">Event name {index + 1}<input className="input w-full" maxLength={150} value={row.event_title} onChange={event => change(row.temporary_id, { event_title: event.target.value })} /></label>
                <label className="block text-sm text-gray-300">Year won {index + 1}<input className="input w-full" type="number" min={1900} max={new Date().getFullYear()} value={row.award_year || ''} onChange={event => change(row.temporary_id, { award_year: Number(event.target.value) })} /></label>
                <label className="block text-sm text-gray-300">Placement {index + 1} (optional)<input className="input w-full" maxLength={50} value={row.placement || ''} onChange={event => change(row.temporary_id, { placement: event.target.value })} /></label>
              </div>
              <details><summary className="text-xs text-gray-400 cursor-pointer">Location and public notes</summary>
                <label className="block text-sm text-gray-300 mt-2">Location {index + 1}<input className="input w-full" maxLength={150} value={row.location || ''} onChange={event => change(row.temporary_id, { location: event.target.value })} /></label>
                <label className="block text-sm text-gray-300 mt-2">Public notes {index + 1}<textarea className="input w-full" maxLength={1000} rows={2} value={row.notes || ''} onChange={event => change(row.temporary_id, { notes: event.target.value })} /></label>
              </details>
            </div>)}
          </fieldset>
        </div>}
    </Modal>, document.body
  );
}
